import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { Alert, Button, CircularProgress, Snackbar } from '@mui/material';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { useAddress } from '../../contexts/AddressContext';
import { useCart } from '../../contexts/CartContext';
import { ApiClient } from '../../lib/api/client';
import { detectCatalogInstance } from '../../lib/catalog/detector';
import { RootState } from '../../store';
import { buildEffectivePerkSnapshot } from '../../utils/perkRules';
import { CartAuthModal } from '../auth/CartAuthModal';

interface AddToCartButtonProps {
  serviceId: string;
  serviceName?: string;
  label?: string; // Custom button label (overrides default "Add to Cart" text)
  catalogInstanceId?: string;
  quantity?: number;
  selectedOption?: any; // For BuyBox selected option
  selectedPerk?: any; // For selected perk information
  selectedAddressId?: string; // For selected service address
  selectedPerkId?: string; // For selected perk ID
  selectedVariants?: Record<string, string>; // For variant selections
  selectedVariantPrice?: number | null; // For variant-specific pricing
  selectedVariantKey?: string; // For variant key (ID)
  serviceConfiguration?: Record<string, any>;
  // Varying price support
  basePrice?: number | { min: number; max: number };
  monthlyRate?: number | { min: number; max: number };
  serviceType?: string; // 'one_time', 'month_to_month', 'contract_custom'
  varyingPriceEnabled?: boolean;
  variant?: 'contained' | 'outlined' | 'text';
  size?: 'small' | 'medium' | 'large';
  fullWidth?: boolean;
  disabled?: boolean;
  onSuccess?: (cartItem: any) => void;
  onError?: (error: string) => void;
}

export function AddToCartButton({
  serviceId,
  serviceName,
  label,
  catalogInstanceId: propCatalogInstanceId, // Accept as prop but override with detected
  quantity = 1,
  selectedOption,
  selectedPerk,
  selectedAddressId,
  selectedPerkId,
  selectedVariants,
  selectedVariantPrice,
  selectedVariantKey,
  serviceConfiguration,
  // Varying price props
  basePrice,
  monthlyRate,
  serviceType,
  varyingPriceEnabled,
  variant = 'contained',
  size = 'medium',
  fullWidth = false,
  disabled = false,
  onSuccess,
  onError,
}: AddToCartButtonProps) {
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [detectedCatalogId, setDetectedCatalogId] = useState<string | null>(
    null,
  ); // null = main marketplace (no specific storefront)

  // Safely use cart context (only available for authenticated customers)
  let openCartModal = () => {};
  try {
    const cartContext = useCart();
    openCartModal = cartContext.openCartModal;
  } catch (error) {
    console.warn('[AddToCartButton] CartContext not available, cart modal will not open:', error);
  }

  // Safely use address context with error handling
  let selectedAddress = null;
  let addresses = [];
  let addressLoading = false;
  let addressError = null;
  let setShowAddAddressDialog = null;

  try {
    const addressContext = useAddress();
    selectedAddress = addressContext?.selectedAddress || null;
    addresses = addressContext?.addresses || [];
    addressLoading = addressContext?.loading || false;
    addressError = addressContext?.error || null;
    setShowAddAddressDialog = addressContext?.setShowAddAddressDialog || null;
  } catch (error) {
    console.warn('[AddToCartButton] AddressContext error:', error);
    addressError = 'Address service unavailable';
  }

  const user = useSelector((state: RootState) => state.auth.user);
  const token = useSelector((state: RootState) => state.auth.token);
  const router = useRouter();
  const isAuthenticated = !!(user && token);

  // Determine address status for click handler logic
  const hasValidAddress = selectedAddress && addresses.length > 0;
  const isAddressLoading = addressLoading;
  const hasAddressError = !!addressError;
  const isCustomer = user?.role === 'customer';

  // Detect catalog instance on mount
  useEffect(() => {
    const detectCatalog = async () => {
      try {
        const catalog = await detectCatalogInstance();
        if (catalog?.id && catalog.id !== 'default' && catalog.id !== 'fallback') {
          console.log('[AddToCartButton] Detected catalog instance:', catalog.id, catalog.name);
          setDetectedCatalogId(catalog.id);
        }
      } catch (error) {
        console.warn('[AddToCartButton] Failed to detect catalog instance:', error);
        // Keep default catalog ID
      }
    };

    detectCatalog();
  }, []);

  // core_rate is NUMERIC dollars. Variant option prices in this button are still cents.
  const formatDollars = (price: number): string => `$${price.toFixed(2)}`;

  // Price formatting helper
  const formatPrice = (price: number | { min: number; max: number } | undefined): string => {
    if (!price) return '$0.00';

    if (typeof price === 'number') {
      // Convert cents to dollars for display
      const dollars = price >= 100 ? price / 100 : price;
      return `$${dollars.toFixed(2)}`;
    } else {
      // Handle min/max range
      const minDollars = price.min >= 100 ? price.min / 100 : price.min;
      const maxDollars = price.max >= 100 ? price.max / 100 : price.max;
      return `$${minDollars.toFixed(2)} - $${maxDollars.toFixed(2)}`;
    }
  };

  // Get price display for button
  const getPriceDisplay = (): string => {
    if (selectedVariantPrice !== null && selectedVariantPrice !== undefined) {
      return formatPrice(selectedVariantPrice);
    }

    // Check for varying prices
    if (varyingPriceEnabled) {
      if (serviceType === 'one_time' && basePrice) {
        return typeof basePrice === 'number' ? formatDollars(basePrice) : formatPrice(basePrice);
      } else if (serviceType !== 'one_time' && monthlyRate) {
        const priceStr = typeof monthlyRate === 'number' ? formatDollars(monthlyRate) : formatPrice(monthlyRate);
        return priceStr + '/mo';
      }
    }

    // Fallback to selected option price or default
    if (selectedOption?.price) {
      return formatPrice(selectedOption.price);
    }

    return '';
  };

  const handleAddToCart = async (event?: React.MouseEvent) => {
    // Prevent event bubbling to avoid triggering card onClick
    event?.stopPropagation();
    if (!serviceId) {
      const errorMsg = 'Service ID is required';
      setSnackbar({ open: true, message: errorMsg, severity: 'error' });
      onError?.(errorMsg);
      return;
    }

    // Check if user is authenticated
    if (!isAuthenticated) {
      // Show account creation modal for anonymous users
      setShowAuthModal(true);
      return;
    }

    // Check if user is a customer (only customers can add to cart)
    if (user?.role !== 'customer') {
      const errorMsg = 'Shopping cart is only available for customers';
      setSnackbar({ open: true, message: errorMsg, severity: 'error' });
      onError?.(errorMsg);
      return;
    }

    // Check if user has a valid address (only for customer users)
    if (isAuthenticated && isCustomer && !hasValidAddress) {
      // Show address dialog for authenticated customers without addresses
      if (setShowAddAddressDialog) {
        setShowAddAddressDialog(true);
      } else {
        const errorMsg = 'Please add a service address before adding items to cart';
        setSnackbar({ open: true, message: errorMsg, severity: 'error' });
        onError?.(errorMsg);
      }
      return;
    }

    try {
      setLoading(true);

      // Use the globally selected address from context, or the prop override
      let addressIdToUse = selectedAddressId;
      if (!addressIdToUse && selectedAddress) {
        addressIdToUse = selectedAddress.id;
      }

      // Get winning perk for the service if not already provided
      let perkIdToUse = selectedPerkId;
      if (!perkIdToUse && selectedPerk) {
        perkIdToUse = selectedPerk.id;
      }
      // Also check selectedOption (used by BuyBox component)
      if (!perkIdToUse && selectedOption && selectedOption.id !== 'base') {
        perkIdToUse = selectedOption.id;
      }

      // Determine variant_price for cart
      let variantPriceToSend = selectedVariantPrice;
      if (!variantPriceToSend && varyingPriceEnabled) {
        if (serviceType === 'one_time' && basePrice) {
          variantPriceToSend = typeof basePrice === 'number' ? basePrice : basePrice.min;
        } else if (serviceType !== 'one_time' && monthlyRate) {
          variantPriceToSend = typeof monthlyRate === 'number' ? monthlyRate : monthlyRate.min;
        }
      }

      const effectivePerkSnapshot = buildEffectivePerkSnapshot(selectedOption || selectedPerk);
      const requestServiceConfiguration = {
        ...(serviceConfiguration || {}),
        ...(effectivePerkSnapshot ? { selected_perk_snapshot: effectivePerkSnapshot } : {}),
      };

      const requestBody = {
        marketplace_service_id: serviceId,
        quantity,
        catalog_instance_id: detectedCatalogId, // Use detected catalog instance
        session_id: null, // Authenticated users don't need session_id
        // Always include selected address, perk, and variant IDs (even if null) for consistent cart matching
        selected_address_id: addressIdToUse || null,
        selected_perk_id: perkIdToUse || null,
        variant_key: selectedVariantKey || null,
        variant_selections: selectedVariants || null, // Send variant selections object
        variant_price: variantPriceToSend || null,
        service_configuration: Object.keys(requestServiceConfiguration).length > 0 ? requestServiceConfiguration : null,
      };

      console.log('[ADD_TO_CART] Adding item to cart:', requestBody);

      const response = await ApiClient.addToCart(requestBody);

      console.log('[ADD_TO_CART] Cart API response:', response);

      // Build success message with service name, variant, and perk information
      let successMessage = '';
      let variantInfo = '';
      let perkInfo = '';

      // Get service name
      if (serviceName) {
        successMessage = serviceName;
      } else if (selectedOption?.partnerName) {
        // For buy box options, use partner name as service name
        successMessage = selectedOption.partnerName;
      } else {
        successMessage = 'Service';
      }

      // Get variant information
      if (selectedVariants && Object.keys(selectedVariants).length > 0) {
        const variantParts = Object.entries(selectedVariants).map(
          ([key, value]) => `${key}: ${value}`,
        );
        variantInfo = variantParts.join(', ');
      }

      // Get perk information
      if (selectedPerk) {
        const perkName = selectedPerk.name || selectedPerk.perk_type || 'perk';
        perkInfo = perkName;
      } else if (selectedOption?.perks?.length > 0) {
        const winningPerk = selectedOption.perks.find(
          (p: any) => p.type === 'financial' || p.rebatePercentage,
        );
        if (winningPerk) {
          perkInfo = winningPerk.rebatePercentage
            ? `${winningPerk.rebatePercentage}% rebate`
            : winningPerk.name || 'perk';
        }
      }

      // Combine service name, variant info, and perk info
      const infoParts = [];
      if (variantInfo) infoParts.push(variantInfo);
      if (serviceConfiguration?.additional_users) {
        const quantityUnitLabel = 'user';
        infoParts.push(
          `extra ${serviceConfiguration.additional_users === 1 ? quantityUnitLabel : `${quantityUnitLabel}s`}: ${serviceConfiguration.additional_users}`,
        );
      }
      if (perkInfo) infoParts.push(perkInfo);

      if (infoParts.length > 0) {
        successMessage += ` (${infoParts.join(', ')})`;
      }

      successMessage += ' added to cart!';
      setSnackbar({ open: true, message: successMessage, severity: 'success' });
      onSuccess?.(response.item);

      // Trigger cart refresh for all cart widgets
      window.dispatchEvent(new CustomEvent('cartUpdated'));

      // Open cart modal immediately after adding item
      setTimeout(() => openCartModal(), 500); // Small delay to let snackbar show first
    } catch (error: any) {
      console.error('[ADD_TO_CART] Error adding to cart:', error);

      const errorMessage = error?.message || 'Failed to add item to cart';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
      onError?.(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Button is only disabled during loading or if explicitly disabled
  const isDisabled = disabled || loading;
  const tooltipTitle = '';

  return (
    <>
      <Button
        variant={variant}
        size={size}
        fullWidth={fullWidth}
        disabled={isDisabled}
        onClick={(event) => handleAddToCart(event)}
        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <ShoppingCartIcon />}
        sx={{
          fontWeight: 'bold',
          minHeight: size === 'large' ? 48 : undefined,
          ...(variant === 'contained' && {
            backgroundColor: 'primary.main',
            '&:hover': {
              backgroundColor: 'primary.dark',
            },
          }),
        }}
      >
        {(() => {
          if (loading) return 'Adding...';
          if (label) return label;
          const priceDisplay = getPriceDisplay();
          return priceDisplay ? `Add to Cart - ${priceDisplay}` : 'Add to Cart';
        })()}
      </Button>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message || 'An error occurred'}
        </Alert>
      </Snackbar>

      <CartAuthModal
        open={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={() => {
          // After successful account creation and cart addition, show success message
          setSnackbar({
            open: true,
            message: serviceName ? `${serviceName} added to cart!` : 'Service added to cart!',
            severity: 'success',
          });
          // Open cart modal after a delay
          setTimeout(() => openCartModal(), 1000);
        }}
        serviceId={serviceId}
        serviceName={serviceName}
      />
    </>
  );
}

export default AddToCartButton;
